import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Sparkles,
  Loader2,
  Download,
  Tag,
  Star,
  AlertCircle,
  CheckCircle,
  Search,
  Filter,
  BarChart3,
  Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PARTY_TYPES = [
  { value: 'plaintiff', label: 'Plaintiff' },
  { value: 'defendant', label: 'Defendant' },
  { value: 'petitioner', label: 'Petitioner' },
  { value: 'respondent', label: 'Respondent' },
  { value: 'appellant', label: 'Appellant' },
  { value: 'appellee', label: 'Appellee' },
  { value: 'government', label: 'Government' },
  { value: 'joint', label: 'Joint' }
];

export default function CaseExhibitsTab({ caseId, caseName }) {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [exhibits, setExhibits] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [arguments_, setArguments] = useState('');
  const [showAnalyzeDialog, setShowAnalyzeDialog] = useState(false);
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [labelDoc, setLabelDoc] = useState(null);
  const [labelForm, setLabelForm] = useState({ party_type: 'plaintiff', numbering_type: 'numeric', exhibit_number: '' });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, [caseId, token]);

  const fetchData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [docsRes, exhibitsRes, analysesRes] = await Promise.all([
        fetch(`${API_URL}/api/cases/${caseId}/documents`, { headers }),
        fetch(`${API_URL}/api/cases/${caseId}/exhibits`, { headers }),
        fetch(`${API_URL}/api/cases/${caseId}/analyses`, { headers })
      ]);
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (exhibitsRes.ok) setExhibits(await exhibitsRes.json());
      if (analysesRes.ok) setAnalyses(await analysesRes.json());
    } catch (error) {
      console.error('Error fetching exhibit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!arguments_.trim()) {
      toast.error('Please enter your case arguments');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/analyze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ arguments: arguments_ })
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Analysis complete! ${result.exhibits_found} exhibits identified.`);
        setShowAnalyzeDialog(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Analysis failed');
      }
    } catch {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAssignLabel = async () => {
    if (!labelDoc) return;
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/documents/${labelDoc.id}/exhibit-label`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(labelForm)
      });
      if (res.ok) {
        toast.success('Exhibit label assigned');
        setShowLabelDialog(false);
        setLabelDoc(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to assign label');
      }
    } catch {
      toast.error('Failed to assign label');
    }
  };

  const handleRemoveLabel = async (docId) => {
    try {
      const res = await fetch(`${API_URL}/api/cases/${caseId}/documents/${docId}/exhibit-label`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Label removed');
        fetchData();
      }
    } catch {
      toast.error('Failed to remove label');
    }
  };

  const handleDownloadReport = async (type) => {
    try {
      const endpoint = type === 'ai' ? 'ai-identification-report' : 'final-exhibits/report';
      const res = await fetch(`${API_URL}/api/cases/${caseId}/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${caseName}_${type}_report.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        toast.error('Report generation failed');
      }
    } catch {
      toast.error('Failed to download report');
    }
  };

  const filteredExhibits = exhibits.filter(e =>
    e.filename?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="case-exhibits-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-heading font-semibold">Exhibit Identification</h3>
          <p className="text-sm text-muted-foreground">
            {documents.length} documents uploaded | {exhibits.length} exhibits identified | {analyses.length} analyses run
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAnalyzeDialog(true)} data-testid="run-analysis-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            Run AI Analysis
          </Button>
          {exhibits.length > 0 && (
            <Button variant="outline" onClick={() => handleDownloadReport('ai')} data-testid="download-report-btn">
              <Download className="w-4 h-4 mr-2" />
              Report
            </Button>
          )}
        </div>
      </div>

      {/* Analysis History */}
      {analyses.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Analysis History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analyses.slice(0, 5).map((analysis, i) => (
                <div key={analysis.id || i} className="flex items-center justify-between text-sm p-2 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-navy" />
                    <span>{analysis.exhibits_found || 0} exhibits found</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {analysis.created_at ? new Date(analysis.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exhibits List */}
      {exhibits.length > 0 ? (
        <>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search exhibits..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
          </div>
          <div className="space-y-2">
            {filteredExhibits.map((doc) => (
              <Card key={doc.id} data-testid={`exhibit-${doc.id}`}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="w-5 h-5 text-navy shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.filename || doc.original_filename}</p>
                      {doc.exhibit_label && (
                        <Badge className="mt-1 text-xs">{doc.exhibit_label}</Badge>
                      )}
                      {doc.relevance_score != null && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-3 h-3 text-amber-500" />
                          <span className="text-xs text-muted-foreground">Relevance: {Math.round(doc.relevance_score * 100)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {!doc.exhibit_label ? (
                      <Button variant="outline" size="sm" onClick={() => { setLabelDoc(doc); setShowLabelDialog(true); }}>
                        <Tag className="w-3.5 h-3.5 mr-1" /> Label
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveLabel(doc.id)} className="text-muted-foreground">
                        Remove Label
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Sparkles className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground mb-1">No exhibits identified yet</p>
            <p className="text-xs text-muted-foreground mb-4">
              {documents.length > 0 
                ? 'Run an AI analysis to identify exhibits from your documents'
                : 'Upload documents first, then run AI analysis'
              }
            </p>
            {documents.length > 0 && (
              <Button variant="outline" onClick={() => setShowAnalyzeDialog(true)}>
                <Sparkles className="w-4 h-4 mr-2" /> Run Analysis
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Analyze Dialog */}
      <Dialog open={showAnalyzeDialog} onOpenChange={setShowAnalyzeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>AI Exhibit Analysis</DialogTitle>
            <DialogDescription>
              Describe your case arguments and the AI will identify which documents support them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Case Arguments</Label>
              <Textarea
                placeholder="Describe your case theory, key arguments, and what you're trying to prove..."
                value={arguments_}
                onChange={(e) => setArguments(e.target.value)}
                rows={6}
                data-testid="analysis-arguments-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnalyzeDialog(false)}>Cancel</Button>
            <Button onClick={handleAnalyze} disabled={analyzing || !arguments_.trim()} data-testid="submit-analysis-btn">
              {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Analyze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Label Dialog */}
      <Dialog open={showLabelDialog} onOpenChange={setShowLabelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Exhibit Label</DialogTitle>
            <DialogDescription>Label "{labelDoc?.filename}" as an exhibit</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Party Type</Label>
              <Select value={labelForm.party_type} onValueChange={(v) => setLabelForm({ ...labelForm, party_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PARTY_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Exhibit Number</Label>
              <Input
                value={labelForm.exhibit_number}
                onChange={(e) => setLabelForm({ ...labelForm, exhibit_number: e.target.value })}
                placeholder="e.g., 1, A, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLabelDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignLabel}>Assign Label</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
